import React, { useState, useEffect, useRef, useMemo } from 'react'
import { apiBase } from '../../lib/api'
import { fmtTime, fmtDate, fmtLocalTs } from '../../utils/formatters'

const hrZoneColor = bpm => bpm < 60 ? '#007aff' : bpm < 100 ? '#34c759' : bpm < 140 ? '#ff9f0a' : '#ff3b30'

async function readVideoMeta(file) {
  // Walk top-level MP4/MOV boxes via File.slice (don't load the huge mdat).
  const slice = async (start, len) => new Uint8Array(await file.slice(start, start + len).arrayBuffer())
  const rd32  = (b, o) => (b[o] * 0x1000000) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]
  const total = file.size
  let pos = 0, moov = null
  while (pos + 8 <= total) {
    const h = await slice(pos, 16)
    let boxSize = rd32(h, 0)
    const type  = String.fromCharCode(h[4], h[5], h[6], h[7])
    if (boxSize === 1) boxSize = rd32(h, 8) * 4294967296 + rd32(h, 12)
    else if (boxSize === 0) boxSize = total - pos
    if (type === 'moov') { moov = await slice(pos, Math.min(boxSize, 16 * 1024 * 1024)); break }
    if (boxSize < 8) break
    pos += boxSize
  }
  if (!moov) return { startMs: null, durationSec: null, hasDate: false }

  const str = new TextDecoder('iso-8859-1').decode(moov)
  let durationSec = null, mvhdMs = null
  const mi = str.indexOf('mvhd')
  if (mi >= 0) {
    const dv = new DataView(moov.buffer, moov.byteOffset + mi)
    const version = moov[mi + 4]
    try {
      if (version === 1) {
        const creation = Number(dv.getBigUint64(8)), timescale = dv.getUint32(24), duration = Number(dv.getBigUint64(28))
        durationSec = timescale ? duration / timescale : null; mvhdMs = (creation - 2082844800) * 1000
      } else {
        const creation = dv.getUint32(8), timescale = dv.getUint32(16), duration = dv.getUint32(20)
        durationSec = timescale ? duration / timescale : null; mvhdMs = (creation - 2082844800) * 1000
      }
    } catch (_) { /* ignore */ }
  }

  // Apple's local creation date with timezone (most reliable for sync).
  let startMs = null, hasDate = false
  const m = str.match(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})([+-]\d{2}:?\d{2}|Z)?/)
  if (m) {
    let tz = m[2] || ''
    if (/^[+-]\d{4}$/.test(tz)) tz = tz.slice(0, 3) + ':' + tz.slice(3)
    const t = Date.parse(m[1] + tz)
    if (!isNaN(t)) { startMs = t; hasDate = true }
  }
  if (startMs == null && mvhdMs && mvhdMs > 9.4e11) startMs = mvhdMs
  return { startMs, durationSec, hasDate }
}

function srtStamp(totalSec) {
  const p2 = n => String(n).padStart(2, '0')
  const h = Math.floor(totalSec / 3600), m = Math.floor((totalSec % 3600) / 60), s = Math.floor(totalSec % 60)
  return `${p2(h)}:${p2(m)}:${p2(s)},000`
}

function buildSrt(readings, { startMs, durationSec, offsetSec = 0, format = 'bpm' }) {
  if (!readings.length) return ''
  const base  = startMs ?? readings[0].tMs
  const total = Math.max(1, Math.floor(durationSec ?? (readings[readings.length - 1].tMs - readings[0].tMs) / 1000))
  const fmt   = hr => format === 'num' ? `${hr}` : `${hr} BPM`
  let ptr = 0, n = 0
  const cues = []
  for (let t = 0; t < total; t++) {
    const target = base + (t + offsetSec) * 1000
    while (ptr + 1 < readings.length && readings[ptr + 1].tMs <= target) ptr++
    let r = readings[ptr]
    if (ptr + 1 < readings.length && Math.abs(readings[ptr + 1].tMs - target) < Math.abs(r.tMs - target)) r = readings[ptr + 1]
    if (!r || Math.abs(r.tMs - target) > 5000) continue   // gap > 5 s → no cue
    n++
    cues.push(`${n}\n${srtStamp(t)} --> ${srtStamp(t + 1)}\n${fmt(r.hr)}\n`)
  }
  return cues.join('\n')
}

export function VideoSrtTab({ token, embedded }) {
  const [sessions, setSessions]   = useState([])
  const [items, setItems]         = useState([])   // one entry per dropped video
  const [format, setFormat]       = useState('bpm')
  const [offsetSec, setOffsetSec] = useState(0)
  const cacheRef = useRef({})                       // sessionId -> readings[]

  useEffect(() => {
    const since = Date.now() - 365 * 24 * 3600 * 1000
    fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSessions((d.sessions || []).map(s => ({
        id: s.session_id, start: new Date(s.start_time).getTime(), end: new Date(s.end_time).getTime(),
        avg: s.avg_hr, count: s.reading_count,
      })).sort((a, b) => b.start - a.start)))
      .catch(() => {})
  }, [token, apiBase])

  const update = (id, patch) => setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it))

  const getReadings = async (sid) => {
    if (cacheRef.current[sid]) return cacheRef.current[sid]
    const d  = await fetch(`${apiBase}/sync?sessionId=${sid}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    const rd = (d.readings || []).map(x => ({ tMs: new Date(x.time).getTime(), hr: x.hr })).filter(x => x.hr > 0).sort((a, b) => a.tMs - b.tMs)
    cacheRef.current[sid] = rd
    return rd
  }

  // Pick the HR session that overlaps the video window the most — fully automatic.
  const matchSession = (startMs, durationSec) => {
    const winEnd = startMs + (durationSec || 0) * 1000
    let best = null, bestOv = 0
    for (const s of sessions) {
      const ov = Math.min(winEnd, s.end) - Math.max(startMs, s.start)
      if (ov > bestOv) { bestOv = ov; best = s }
    }
    return best
  }

  const processOne = async (file) => {
    const id = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`
    setItems(prev => [...prev, { id, name: file.name, status: 'reading' }])
    try {
      const m = await readVideoMeta(file)
      // Fallback: no embedded date → estimate from the file's modified time
      // (phones set it right after recording ends: start ≈ mtime − duration).
      let startMs = m.startMs, estimated = false
      if (!startMs && file.lastModified && m.durationSec) { startMs = file.lastModified - m.durationSec * 1000; estimated = true }
      if (!startMs)  { update(id, { status: 'nodate',  ...m }); return }
      const best = matchSession(startMs, m.durationSec)
      if (!best)       { update(id, { status: 'nomatch', ...m, startMs, estimated }); return }
      update(id, { status: 'loading', ...m, startMs, estimated, sessionId: best.id, sessionStart: best.start })
      const rd = await getReadings(best.id)
      update(id, { status: rd.length ? 'ok' : 'empty', readings: rd })
    } catch (e) { update(id, { status: 'error', error: e.message }) }
  }

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && /video|\.(mov|mp4|m4v)$/i.test((f.type || '') + ' ' + f.name))
    for (const f of files) await processOne(f)   // sequential → reuses the session cache
  }

  // Manual fallback only for videos whose date/session couldn't be detected.
  const assignSession = async (id, sid) => {
    if (!sid) return
    const s = sessions.find(x => x.id === sid)
    update(id, { status: 'loading', sessionId: sid, sessionStart: s?.start })
    const rd = await getReadings(sid)
    update(id, { status: rd.length ? 'ok' : 'empty', readings: rd })
  }

  const built = useMemo(() => items.map(it => {
    if (it.status !== 'ok' || !it.readings?.length) return { ...it, srt: '', cues: 0 }
    const startMs = it.startMs ?? it.sessionStart ?? it.readings[0].tMs
    const durationSec = it.durationSec ?? ((it.readings[it.readings.length - 1].tMs - it.readings[0].tMs) / 1000)
    const srt = buildSrt(it.readings, { startMs, durationSec, offsetSec, format })
    return { ...it, srt, cues: srt ? srt.trim().split(/\n\s*\n/).length : 0 }
  }), [items, offsetSec, format])

  const downloadOne = (it) => {
    if (!it.srt) return
    const url = URL.createObjectURL(new Blob([it.srt], { type: 'application/x-subrip' }))
    const a = document.createElement('a')
    a.href = url; a.download = `pulso_${it.name.replace(/\.[^.]+$/, '')}.srt`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }
  const downloadAll = () => built.filter(it => it.srt).forEach((it, i) => setTimeout(() => downloadOne(it), i * 250))

  const okCount = built.filter(it => it.srt).length
  const card    = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 14 }
  const fmtOpts = [{ k: 'num', l: '68' }, { k: 'bpm', l: '68 BPM' }]
  const dur     = s => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`

  const statusLine = (it) => {
    switch (it.status) {
      case 'reading': return <span style={{ color: 'var(--text-faint)' }}>Leyendo metadatos…</span>
      case 'loading': return <span style={{ color: 'var(--text-faint)' }}>Buscando tu pulso de ese momento…</span>
      case 'nodate':  return <span style={{ color: '#ff9f0a' }}>Sin fecha en los metadatos — elegí la sesión a mano.</span>
      case 'nomatch': return <span style={{ color: '#ff9f0a' }}>No hay pulso registrado para {fmtLocalTs(it.startMs)} — elegí a mano.</span>
      case 'empty':   return <span style={{ color: '#ff9f0a' }}>Esa sesión no tiene pulso válido.</span>
      case 'error':   return <span style={{ color: '#ff3b30' }}>Error: {it.error}</span>
      case 'ok':      return <span style={{ color: '#34c759' }}>✓ {it.startMs ? fmtLocalTs(it.startMs) + ' · ' : ''}{it.durationSec ? dur(it.durationSec) + ' · ' : ''}{it.cues} subtítulos</span>
      default: return null
    }
  }

  return (
    <div style={{ maxWidth: embedded ? '100%' : 640, margin: '0 auto' }}>
      {!embedded && <h2 style={{ fontSize: 20, fontWeight: 800, margin: '4px 0 4px' }}>Pulso para tus videos</h2>}
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
        <strong>Para creadores de contenido:</strong> soltá uno o varios videos. Detecta solo la hora de grabación
        de cada uno por sus metadatos, busca tu pulso de ese rango y arma el subtítulo <strong>.srt</strong> — sin
        que elijas nada. Lo importás y estilizás en Premiere. Todo en tu navegador; los videos no se suben.
      </p>

      {/* Drop / select (varios) */}
      <label style={{ display: 'block', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '22px', textAlign: 'center', cursor: 'pointer', background: 'var(--field-bg)', marginBottom: 14 }}
        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onFiles(e.dataTransfer.files) }}>
        <input type="file" accept="video/*,.mov,.mp4,.m4v" multiple style={{ display: 'none' }} onChange={e => onFiles(e.target.files)} />
        <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>Soltá o elegí tus videos</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>.mov / .mp4 · uno o varios a la vez</span>
      </label>

      {/* Formato + sincronía (global) */}
      {items.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {fmtOpts.map(o => (
              <button key={o.k} onClick={() => setFormat(o.k)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                  background: format === o.k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: format === o.k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{o.l}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 86 }}>Desfase: {offsetSec > 0 ? '+' : ''}{offsetSec}s</span>
            <input type="range" min={-60} max={60} step={1} value={offsetSec} onChange={e => setOffsetSec(Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30', cursor: 'pointer' }} />
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '6px 0 0' }}>El formato y el desfase se aplican a todos los videos.</p>
        </div>
      )}

      {/* Resultado por video */}
      {built.map(it => (
        <div key={it.id} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</p>
              <p style={{ fontSize: 12, margin: '4px 0 0' }}>{statusLine(it)}</p>
            </div>
            {it.srt && (
              <button onClick={() => downloadOne(it)} style={{ flexShrink: 0, padding: '9px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: '#ff3b30', color: '#fff' }}>Descargar .srt</button>
            )}
          </div>
          {(it.status === 'nodate' || it.status === 'nomatch' || it.status === 'empty') && (
            <select defaultValue="" onChange={e => assignSession(it.id, e.target.value)}
              style={{ marginTop: 10, width: '100%', background: 'var(--chip-bg)', border: '1px solid var(--field-border)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
              <option value="">— Elegir sesión a mano (respaldo) —</option>
              {sessions.map(s => <option key={s.id} value={s.id}>{fmtLocalTs(s.start)} · {s.count} lecturas</option>)}
            </select>
          )}
        </div>
      ))}

      {okCount > 1 && (
        <button onClick={downloadAll} style={{ width: '100%', padding: '12px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, background: 'var(--btn-strong)', color: '#fff', marginBottom: 14 }}>
          Descargar todos ({okCount})
        </button>
      )}

      {items.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: 0, lineHeight: 1.5 }}>
          En Premiere: Archivo → Importar → el .srt → arrastralo al timeline. Estilo en el panel de Subtítulos; para control total, clic derecho → “Convertir subtítulos en gráfico”.
        </p>
      )}
    </div>
  )
}

// Shared overlay painter — used by both the live preview and the export, so they
// look identical. Sizes are fractions of the frame height → resolution-independent.
const OVERLAY_FONTS = {
  system:    { label: 'Sans',      css: '-apple-system, system-ui, sans-serif' },
  rounded:   { label: 'Redonda',   css: '"SF Pro Rounded", "Nunito", system-ui, sans-serif' },
  mono:      { label: 'Mono',      css: '"SF Mono", "Roboto Mono", ui-monospace, monospace' },
  condensed: { label: 'Condensada',css: '"Roboto Condensed", "Arial Narrow", system-ui, sans-serif' },
}
function hexLum(hex) {
  const h = (hex || '#fff').replace('#', '')
  const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16)
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

function drawHrOverlay(ctx, W, H, hr, o) {
  if (hr == null) return
  const fontPx = Math.max(10, Math.round(H * o.size))
  const text = `${hr}${o.label ? ' BPM' : ''}`
  ctx.font = `${o.weight || 800} ${fontPx}px ${(OVERLAY_FONTS[o.font] || OVERLAY_FONTS.system).css}`
  ctx.textBaseline = 'middle'
  const tw = ctx.measureText(text).width
  const padX = fontPx * 0.5, padY = fontPx * 0.32
  const boxW = tw + padX * 2, boxH = fontPx + padY * 2
  const m = Math.round(H * 0.045)
  const right = W - boxW - m, bottom = H - boxH - m, cx = (W - boxW) / 2
  let x, y
  switch (o.pos) {
    case 'tl': x = m;     y = m; break
    case 'tr': x = right; y = m; break
    case 'tc': x = cx;    y = m; break
    case 'bl': x = m;     y = bottom; break
    case 'bc': x = cx;    y = bottom; break
    default:   x = right; y = bottom            // 'br'
  }
  // Fine X/Y nudge from the chosen anchor (fraction of W/H), clamped on-screen
  x += (o.dx || 0) * W
  y += (o.dy || 0) * H
  x = Math.max(0, Math.min(W - boxW, x))
  y = Math.max(0, Math.min(H - boxH, y))
  if (o.pill) {
    ctx.save()
    ctx.globalAlpha = o.pillOpacity
    ctx.fillStyle = o.pillColor || '#000000'
    const r = boxH * 0.3
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, boxW, boxH, r); ctx.fill() }
    else ctx.fillRect(x, y, boxW, boxH)
    ctx.restore()
  }
  const fill = o.zoneColor ? hrZoneColor(hr) : o.color
  const tx = x + padX, ty = y + boxH / 2
  ctx.textAlign = 'left'
  if (o.outline) {
    ctx.lineWidth = Math.max(2, fontPx * 0.11)
    ctx.lineJoin = 'round'
    ctx.strokeStyle = hexLum(fill) > 0.5 ? 'rgba(0,0,0,0.92)' : 'rgba(255,255,255,0.92)'
    ctx.strokeText(text, tx, ty)
  } else if (!o.pill) {
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = fontPx * 0.2; ctx.shadowOffsetY = fontPx * 0.04
  }
  ctx.fillStyle = fill
  ctx.fillText(text, tx, ty)
  ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
}

const BURN_PRESETS = [
  { name: 'Minimal',  opt: { pill: false, outline: true,  heart: true,  label: false, weight: 700, size: 0.075, color: '#ffffff', zoneColor: false } },
  { name: 'Cápsula',  opt: { pill: true,  outline: false, heart: true,  label: true,  weight: 800, size: 0.085, color: '#ffffff', zoneColor: false, pillColor: '#000000', pillOpacity: 0.45 } },
  { name: 'Neón',     opt: { pill: false, outline: true,  heart: true,  label: true,  weight: 900, size: 0.09,  color: '#39ff14', zoneColor: false } },
  { name: 'Por zona', opt: { pill: true,  outline: false, heart: true,  label: true,  weight: 800, size: 0.085, zoneColor: true,  pillColor: '#000000', pillOpacity: 0.4 } },
  { name: 'Grande',   opt: { pill: false, outline: true,  heart: true,  label: true,  weight: 900, size: 0.13,  color: '#ffffff', zoneColor: false } },
]

// ── Burn/overlay persistent state (module scope) ────────────────────────────
// The tab unmounts when you navigate away; everything lives here so the queue,
// matches and config survive — and the export worker keeps running because it
// uses its own detached <video> element, not the preview player.
const DEFAULT_BURN_OPT = { pos: 'br', dx: 0, dy: 0, size: 0.085, color: '#ffffff', heart: true, label: true, pill: true, pillOpacity: 0.45, font: 'system', weight: 800, outline: false, zoneColor: false, pillColor: '#000000' }
export const burnMem = {
  videos: [], activeId: null, opt: { ...DEFAULT_BURN_OPT }, bgMode: 'transparent',
  exporting: false, doneCount: 0, progress: 0, queueRunning: false,
  subs: new Set(),
  emit() { this.subs.forEach(fn => { try { fn() } catch {} }) },
  subscribe(fn) { this.subs.add(fn); return () => this.subs.delete(fn) },
}
const burnPatch = (id, patch) => { burnMem.videos = burnMem.videos.map(v => v.id === id ? { ...v, ...patch } : v); burnMem.emit() }
const burnReadingsCache = new Map()   // sessionId -> readings[] (survives remounts)

let burnExportEl = null               // detached player used ONLY for exporting
function getBurnExportEl() {
  if (!burnExportEl) { burnExportEl = document.createElement('video'); burnExportEl.playsInline = true; burnExportEl.preload = 'auto'; burnExportEl.muted = true }
  return burnExportEl
}

const burnEnsureLoaded = (v) => new Promise((res, rej) => {
  const el = getBurnExportEl()
  if (el.src !== v.url) { el.src = v.url; el.load() }
  let tries = 0
  const t = setInterval(() => {
    if (el.readyState >= 2 && el.videoWidth > 0) { clearInterval(t); res(el) }
    else if (++tries > 300) { clearInterval(t); rej(new Error('el video no cargó')) }
  }, 50)
})

const burnHrAt = (v, tSec) => {
  if (!v || !v.readings?.length || !v.meta?.startMs) return null
  const target = v.meta.startMs + (tSec + (v.offsetSec || 0)) * 1000
  const rds = v.readings
  let lo = 0, hi = rds.length - 1
  while (lo < hi) { const mid = (lo + hi) >> 1; if (rds[mid].tMs < target) lo = mid + 1; else hi = mid }
  let r = rds[lo]
  if (lo > 0 && Math.abs(rds[lo - 1].tMs - target) < Math.abs(r.tMs - target)) r = rds[lo - 1]
  return r && Math.abs(r.tMs - target) < 5000 ? r.hr : null
}

async function burnRunExport(v, mode) {     // 'burn' | 'overlay' — uses the detached element
  const el = getBurnExportEl()
  if (!v.readings?.length) throw new Error('sin pulso sincronizado')
  if (typeof MediaRecorder === 'undefined') throw new Error('Tu navegador no soporta exportar video. Usá Chrome.')
  const W = el.videoWidth, H = el.videoHeight
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')
  const cstream = canvas.captureStream(30)
  const tracks = [...cstream.getVideoTracks()]
  if (mode === 'burn') {
    try { const vs = el.captureStream ? el.captureStream() : el.mozCaptureStream(); const at = vs.getAudioTracks()[0]; if (at) tracks.push(at) } catch (_) { /* no audio */ }
  }
  const stream = new MediaStream(tracks)
  const mime = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm'
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 12_000_000 })
  const chunks = []; rec.ondataavailable = e => { if (e.data.size) chunks.push(e.data) }
  const stopped = new Promise(res => { rec.onstop = res })

  el.pause(); el.currentTime = 0
  await new Promise(r => { el.onseeked = r })

  const pump = () => {
    const bgMode = burnMem.bgMode
    if (mode === 'overlay') {
      ctx.clearRect(0, 0, W, H)
      if (bgMode !== 'transparent') { ctx.fillStyle = bgMode === 'green' ? '#00ff00' : '#000000'; ctx.fillRect(0, 0, W, H) }
    } else ctx.drawImage(el, 0, 0, W, H)
    drawHrOverlay(ctx, W, H, burnHrAt(v, el.currentTime), burnMem.opt)
    const p = Math.min(1, el.currentTime / (el.duration || 1))
    if (Math.abs(p - burnMem.progress) > 0.01) { burnMem.progress = p; burnMem.emit() }
  }
  const useRVFC = 'requestVideoFrameCallback' in HTMLVideoElement.prototype
  let stopLoop = false
  const loop = () => { if (stopLoop) return; pump(); useRVFC ? el.requestVideoFrameCallback(loop) : requestAnimationFrame(loop) }

  pump()                     // prime first frame
  rec.start()
  await el.play()
  loop()
  await new Promise(res => { el.onended = () => { stopLoop = true; res() } })
  pump()                     // final frame
  rec.stop()
  await stopped
  el.pause()

  const blob = new Blob(chunks, { type: mime })
  const url = URL.createObjectURL(blob)
  const name = `${mode === 'burn' ? 'pulso_quemado' : 'pulso_overlay'}_${(v.name || 'video').replace(/\.[^.]+$/, '')}.webm`
  // Intenta la descarga automática, pero NO destruye el resultado: queda
  // guardado en la cola (botón Descargar) por si estabas en otra pestaña
  // cuando terminó — no se pierde nada al navegar.
  try { const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove() } catch { /* la cola conserva el archivo */ }
  return { url, name, size: blob.size }
}

// Queue worker: processes 'pendiente' videos one after another. Runs at module
// scope — navigating to another tab does NOT stop it.
async function burnStartWorker(mode) {
  if (burnMem.queueRunning) return
  burnMem.queueRunning = true; burnMem.exporting = true; burnMem.doneCount = 0; burnMem.emit()
  while (true) {
    await new Promise(r => setTimeout(r, 80))   // let state settle between items
    const next = burnMem.videos.find(v => v.exportState === 'pendiente')
    if (!next) break
    burnMem.activeId = next.id
    burnPatch(next.id, { exportState: 'procesando' })
    try {
      await burnEnsureLoaded(next)
      const fresh = burnMem.videos.find(x => x.id === next.id) ?? next   // pick up offset edits
      const out = await burnRunExport(fresh, mode)
      burnPatch(next.id, { exportState: 'listo', resultUrl: out.url, resultName: out.name, resultSize: out.size })
    } catch (e) {
      burnPatch(next.id, { exportState: 'error', note: 'Error al exportar: ' + e.message })
    }
    burnMem.doneCount++; burnMem.emit()
  }
  burnMem.queueRunning = false; burnMem.exporting = false; burnMem.progress = 0; burnMem.emit()
}

export function VideoBurnTab({ token }) {
  const [sessions, setSessions] = useState([])
  // Re-render whenever the module-scope state changes (queue progress, patches…)
  const [, force] = useState(0)
  useEffect(() => burnMem.subscribe(() => force(x => x + 1)), [])

  const videos    = burnMem.videos
  const opt       = burnMem.opt
  const bgMode    = burnMem.bgMode
  const exporting = burnMem.exporting
  const progress  = burnMem.progress
  const doneCount = burnMem.doneCount
  const setOpt      = (fnOrVal) => { burnMem.opt = typeof fnOrVal === 'function' ? fnOrVal(burnMem.opt) : fnOrVal; burnMem.emit() }
  const setO        = (k, val) => setOpt(o => ({ ...o, [k]: val }))
  const setBgMode   = (v) => { burnMem.bgMode = v; burnMem.emit() }
  const setActiveId = (id) => { burnMem.activeId = id; burnMem.emit() }

  const videoRef  = useRef(null)    // preview player (separate from the export element)
  const canvasRef = useRef(null)
  const rafRef    = useRef(null)
  const sessionsRef   = useRef([])
  const sessionsReady = useRef(null)

  useEffect(() => {
    const since = Date.now() - 365 * 24 * 3600 * 1000
    sessionsReady.current = fetch(`${apiBase}/sync?since=${since}&sessionsOnly=1`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = (d.sessions || []).map(s => ({ id: s.session_id, start: new Date(s.start_time).getTime(), end: new Date(s.end_time).getTime() }))
        sessionsRef.current = list; setSessions(list); return list
      })
      .catch(() => [])
  }, [token, apiBase])

  const loadReadings = async (sessionId) => {
    if (burnReadingsCache.has(sessionId)) return burnReadingsCache.get(sessionId)
    const d  = await fetch(`${apiBase}/sync?sessionId=${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json())
    const rd = (d.readings || []).map(x => ({ tMs: new Date(x.time).getTime(), hr: x.hr })).filter(x => x.hr > 0).sort((a, b) => a.tMs - b.tMs)
    burnReadingsCache.set(sessionId, rd)
    return rd
  }

  // Best overlapping session; if none overlaps, fall back to the NEAREST session
  // within 24 h (video filmed outside the recording window, or clock-shifted
  // metadata) — the video gets aligned to that session's start.
  const matchSession = (startMs, durationSec) => {
    const winEnd = startMs + (durationSec || 0) * 1000
    let best = null, bestOv = 0
    for (const s of sessionsRef.current) { const ov = Math.min(winEnd, s.end) - Math.max(startMs, s.start); if (ov > bestOv) { bestOv = ov; best = s } }
    if (best) return { session: best, aligned: false }
    let nearest = null, bestGap = Infinity
    for (const s of sessionsRef.current) {
      const gap = Math.max(s.start - winEnd, startMs - s.end, 0)
      if (gap < bestGap) { bestGap = gap; nearest = s }
    }
    if (nearest && bestGap <= 24 * 3600 * 1000) return { session: nearest, aligned: true }
    return { session: null, aligned: false }
  }

  const processVideo = async (id, f) => {
    try {
      const m = await readVideoMeta(f)
      // Fallback: no embedded date → estimate from the file's modified time
      // (phones set it right after recording ends: start ≈ mtime − duration).
      let startMs = m.startMs, estimated = false
      if (!startMs && f.lastModified && m.durationSec) { startMs = f.lastModified - m.durationSec * 1000; estimated = true }
      if (!startMs) { burnPatch(id, { status: 'manual', meta: m, note: 'Sin fecha en los metadatos — elegí la sesión abajo' }); return }
      await sessionsReady.current
      const { session: best, aligned } = matchSession(startMs, m.durationSec)
      if (!best) { burnPatch(id, { status: 'manual', meta: { ...m, startMs, estimated }, note: `Sin pulso cerca del ${fmtDate(startMs)} ${fmtTime(startMs)} — elegí la sesión abajo` }); return }
      const rd = await loadReadings(best.id)
      if (!rd.length) { burnPatch(id, { status: 'manual', meta: { ...m, startMs, estimated }, note: 'La sesión detectada no tiene pulso válido — elegí otra abajo' }); return }
      // Aligned fallback: pretend the video starts with the session (fine-tune with Desfase)
      const meta = { ...m, startMs: aligned ? best.start : startMs, estimated }
      burnPatch(id, {
        status: 'listo', meta, readings: rd,
        note: aligned
          ? `Sin pulso a la hora exacta del video (${fmtDate(startMs)} ${fmtTime(startMs)}) — sincronicé con la sesión más cercana (${fmtTime(best.start)}); ajustá el Desfase`
          : `${fmtDate(startMs)} ${fmtTime(startMs)}${estimated ? ' (estimada por fecha de archivo)' : ''} · ${rd.length} lecturas`,
        // If an export queue is running, new videos join the line automatically
        exportState: burnMem.queueRunning ? 'pendiente' : null,
      })
    } catch (e) { burnPatch(id, { status: 'error', note: 'Error: ' + e.message }) }
  }

  const onVideos = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && /video|\.(mov|mp4|m4v|webm)$/i.test((f.type || '') + ' ' + f.name))
    if (!files.length) return
    const items = files.map(f => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      file: f, url: URL.createObjectURL(f), name: f.name,
      meta: null, readings: [], offsetSec: 0,
      status: 'analizando', note: 'Leyendo metadatos…', exportState: null,
    }))
    burnMem.videos = [...burnMem.videos, ...items]
    if (!burnMem.activeId) burnMem.activeId = items[0].id
    burnMem.emit()
    for (const it of items) await processVideo(it.id, it.file)   // sequential → reuses caches
  }

  const removeVideo = (id) => {
    const v = burnMem.videos.find(x => x.id === id)
    if (v?.url && v.exportState !== 'procesando') URL.revokeObjectURL(v.url)
    if (v?.resultUrl) URL.revokeObjectURL(v.resultUrl)   // libera el export guardado
    burnMem.videos = burnMem.videos.filter(x => x.id !== id)
    if (burnMem.activeId === id) burnMem.activeId = burnMem.videos[0]?.id ?? null
    burnMem.emit()
  }

  // Manual sync: pick (or override) the session whose pulse to burn.
  const pickSession = async (videoId, sessionId) => {
    const s = sessionsRef.current.find(x => x.id === sessionId)
    if (!s) return
    burnPatch(videoId, { status: 'analizando', note: 'Cargando pulso…' })
    try {
      const rd = await loadReadings(sessionId)
      const v = burnMem.videos.find(x => x.id === videoId)
      burnPatch(videoId, rd.length
        ? { status: 'listo', readings: rd, offsetSec: 0, meta: { ...(v?.meta || {}), startMs: s.start }, note: `Sesión elegida a mano (${rd.length} lecturas) — ajustá el Desfase si hace falta`, exportState: burnMem.queueRunning ? 'pendiente' : null }
        : { status: 'manual', note: 'Esa sesión no tiene pulso válido — probá con otra' })
    } catch (e) { burnPatch(videoId, { status: 'error', note: 'Error: ' + e.message }) }
  }

  const active = videos.find(v => v.id === burnMem.activeId) ?? videos[0] ?? null
  const readyVideos = videos.filter(v => v.status === 'listo' && v.readings.length)

  // Live preview overlay on top of the playing video (the ACTIVE one).
  useEffect(() => {
    const v = videoRef.current, c = canvasRef.current
    if (!v || !c || !active?.url) return
    let stop = false
    const draw = () => {
      if (stop) return
      const W = c.clientWidth || 320, H = c.clientHeight || 180
      if (c.width !== W) c.width = W
      if (c.height !== H) c.height = H
      const ctx = c.getContext('2d')
      ctx.clearRect(0, 0, W, H)
      drawHrOverlay(ctx, W, H, burnHrAt(active, v.currentTime), opt)
      rafRef.current = requestAnimationFrame(draw)
    }
    draw()
    return () => { stop = true; cancelAnimationFrame(rafRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opt, videos, burnMem.activeId])

  // Enqueue every ready video (skipping already-exported ones; if all are
  // exported, re-export everything) and start the worker.
  const runBatch = (mode) => {
    const ready = burnMem.videos.filter(v => v.status === 'listo' && v.readings.length)
    let ids = ready.filter(v => v.exportState !== 'listo' && v.exportState !== 'procesando').map(v => v.id)
    if (!ids.length) ids = ready.map(v => v.id)
    if (!ids.length) return
    burnMem.videos = burnMem.videos.map(v => ids.includes(v.id) ? { ...v, exportState: 'pendiente' } : v)
    burnMem.emit()
    burnStartWorker(mode)
  }

  const card    = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 14 }
  const posOpts = [['tl', 'Arr Izq'], ['tc', 'Arriba'], ['tr', 'Arr Der'], ['bl', 'Abj Izq'], ['bc', 'Abajo'], ['br', 'Abj Der']]
  const fieldLabel = { fontSize: 12, color: 'var(--text-muted)', margin: '0 0 6px' }
  const rowS = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }
  const chip = on => ({ fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 20, padding: '7px 13px', cursor: 'pointer', fontFamily: 'inherit', background: on ? 'var(--btn-strong)' : 'var(--chip-bg)', color: on ? '#fff' : 'var(--text-faint)', transition: 'all .12s' })

  const STATUS_BADGE = {
    analizando: { txt: 'Analizando…', color: 'var(--text-faint)' },
    listo:      { txt: '✓ pulso',  color: '#34c759' },
    manual:     { txt: 'Manual', color: '#ff9f0a' },
    error:      { txt: '✗ error',  color: '#ff3b30' },
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
        <strong>Para creadores de contenido:</strong> cargá uno o varios videos — la hora se detecta sola de los
        metadatos y el pulso se sincroniza automáticamente. Elegí un estilo y se aplica igual a todos.
        Todo local; los videos no se suben.
      </p>

      {/* Drop — acepta varios */}
      <label style={{ display: 'block', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '20px', textAlign: 'center', cursor: 'pointer', background: 'var(--field-bg)', marginBottom: 14 }}
        onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); onVideos(e.dataTransfer.files) }}>
        <input type="file" accept="video/*,.mov,.mp4,.m4v" multiple style={{ display: 'none' }} onChange={e => { onVideos(e.target.files); e.target.value = '' }} />
        <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>
          {videos.length ? `${videos.length} video${videos.length !== 1 ? 's' : ''} — agregar más` : 'Soltá o elegí tus videos (podés varios)'}
        </span>
      </label>

      {/* Cola de videos */}
      {videos.length > 0 && (
        <div style={{ ...card, padding: 8 }}>
          {videos.map(v => {
            const badge = STATUS_BADGE[v.status] ?? STATUS_BADGE.analizando
            const isActive = active?.id === v.id
            return (
              <div key={v.id} onClick={() => setActiveId(v.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, cursor: 'pointer',
                  background: isActive ? 'var(--track)' : 'transparent', border: `1px solid ${isActive ? 'var(--border-strong)' : 'transparent'}` }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', flexShrink: 0 }}>[VID]</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.note}</p>
                  {v.status === 'manual' && sessions.length > 0 && (
                    <select value="" onClick={e => e.stopPropagation()} onChange={e => e.target.value && pickSession(v.id, e.target.value)}
                      style={{ marginTop: 6, width: '100%', maxWidth: 320, background: 'var(--field-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', color: 'var(--text)' }}>
                      <option value="">Elegí la sesión a sincronizar…</option>
                      {[...sessions].sort((a, b) => b.start - a.start).slice(0, 50).map(s => (
                        <option key={s.id} value={s.id}>{fmtDate(s.start)} · {fmtTime(s.start)}</option>
                      ))}
                    </select>
                  )}
                </div>
                {v.exportState === 'procesando'
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#007aff', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>↑ {Math.round(progress * 100)}%</span>
                  : v.exportState === 'pendiente'
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#5856d6', flexShrink: 0 }}>En fila</span>
                  : v.exportState === 'listo'
                  ? (v.resultUrl
                      ? <a href={v.resultUrl} download={v.resultName || 'pulso.webm'} onClick={e => e.stopPropagation()}
                          style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#34c759', borderRadius: 8, padding: '5px 10px', textDecoration: 'none', flexShrink: 0 }}>
                          Descargar{v.resultSize ? ` (${Math.round(v.resultSize / 1048576)} MB)` : ''}
                        </a>
                      : <span style={{ fontSize: 11, fontWeight: 700, color: '#34c759', flexShrink: 0 }}>✓ exportado</span>)
                  : <span style={{ fontSize: 11, fontWeight: 700, color: badge.color, flexShrink: 0 }}>{badge.txt}</span>}
                {v.exportState !== 'procesando' && (
                  <button onClick={e => { e.stopPropagation(); removeVideo(v.id) }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-fainter)', padding: '2px 4px', flexShrink: 0 }}>✕</button>
                )}
              </div>
            )
          })}
          {videos.length > 1 && (
            <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '6px 10px 4px' }}>
              Tocá un video para previsualizarlo — el estilo que configures abajo se aplica a todos. La lista se guarda aunque cambies de pestaña.
            </p>
          )}
        </div>
      )}

      {/* Preview del video activo */}
      {active?.url && (
        <div style={{ position: 'relative', width: '100%', borderRadius: 14, overflow: 'hidden', marginBottom: 14, background: '#000' }}>
          <video ref={videoRef} src={active.url} controls playsInline style={{ display: 'block', width: '100%' }} />
          <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
        </div>
      )}

      {/* Customization panel — UNA config para todos los videos */}
      {active?.url && (
        <div style={card}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' }}>
            Personalización{videos.length > 1 ? ' (se aplica a todos los videos)' : ''}
          </p>

          {/* Estilos rápidos (presets) */}
          <p style={fieldLabel}>Estilos rápidos</p>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
            {BURN_PRESETS.map(p => (
              <button key={p.name} onClick={() => setOpt(o => ({ ...o, ...p.opt }))}
                style={{ fontSize: 12, fontWeight: 600, border: '1px solid var(--field-border)', borderRadius: 20, padding: '7px 13px', cursor: 'pointer', fontFamily: 'inherit', background: 'var(--card-bg)', color: 'var(--text)' }}>{p.name}</button>
            ))}
          </div>

          {/* Posición: anclaje rápido */}
          <p style={fieldLabel}>Posición</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
            {posOpts.map(([k, ic]) => (
              <button key={k} onClick={() => setOpt(o => ({ ...o, pos: k, dx: 0, dy: 0 }))} style={{ padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 17, background: opt.pos === k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: opt.pos === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)', transition: 'all .12s' }}>{ic}</button>
            ))}
          </div>

          {/* Alineación fina X / Y */}
          <div style={{ background: 'var(--field-bg)', border: '1px solid #f0f0f3', borderRadius: 11, padding: '10px 12px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Alineación fina</span>
              <button onClick={() => setOpt(o => ({ ...o, dx: 0, dy: 0 }))} style={{ fontSize: 11, color: '#007aff', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Volver al ancla</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', minWidth: 14 }}>X</span>
              <input type="range" min={-0.5} max={0.5} step={0.005} value={opt.dx} onChange={e => setO('dx', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
              <span style={{ fontSize: 11, color: 'var(--text-soft)', minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{opt.dx > 0 ? '+' : ''}{Math.round(opt.dx * 100)}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', minWidth: 14 }}>Y</span>
              <input type="range" min={-0.5} max={0.5} step={0.005} value={opt.dy} onChange={e => setO('dy', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
              <span style={{ fontSize: 11, color: 'var(--text-soft)', minWidth: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{opt.dy > 0 ? '+' : ''}{Math.round(opt.dy * 100)}%</span>
            </div>
          </div>

          {/* Tamaño */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Tamaño</span>
            <input type="range" min={0.04} max={0.16} step={0.005} value={opt.size} onChange={e => setO('size', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} />
          </div>

          {/* Fuente */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Fuente</span>
            <select value={opt.font} onChange={e => setO('font', e.target.value)} style={{ flex: 1, background: 'var(--chip-bg)', border: '1px solid var(--field-border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}>
              {Object.entries(OVERLAY_FONTS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
            </select>
          </div>

          {/* Grosor */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Grosor</span>
            <div style={{ display: 'flex', gap: 6, flex: 1 }}>
              {[[400, 'Fino'], [700, 'Negrita'], [900, 'Black']].map(([w, l]) => (
                <button key={w} onClick={() => setO('weight', w)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: opt.weight === w ? 'var(--btn-strong)' : 'var(--chip-bg)', color: opt.weight === w ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{l}</button>
              ))}
            </div>
          </div>

          {/* Color del texto */}
          <div style={rowS}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Color</span>
            <input type="color" value={opt.color} onChange={e => setO('color', e.target.value)} style={{ width: 40, height: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', padding: 0 }} />
            <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>color del texto</span>
          </div>

          {/* Estilo: chips on/off */}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 14 }}>
            {[['heart', 'Icono'], ['label', 'BPM'], ['pill', 'Fondo'], ['outline', 'Contorno'], ['zoneColor', 'Color por zona']].map(([k, l]) => (
              <button key={k} onClick={() => setO(k, !opt[k])} style={chip(opt[k])}>{l}</button>
            ))}
          </div>

          {/* Fondo (pill) */}
          {opt.pill && (
            <div style={rowS}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Fondo</span>
              <input type="color" value={opt.pillColor} onChange={e => setO('pillColor', e.target.value)} style={{ width: 40, height: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', padding: 0 }} />
              <input type="range" min={0} max={0.85} step={0.05} value={opt.pillOpacity} onChange={e => setO('pillOpacity', Number(e.target.value))} style={{ flex: 1, accentColor: '#ff3b30' }} title="Opacidad del fondo" />
            </div>
          )}

          {/* Desfase (sincronización) — por video */}
          <div style={{ ...rowS, marginBottom: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 70 }}>Desfase{videos.length > 1 ? ' (este video)' : ''}</span>
            <input type="range" min={-300} max={300} step={1} value={active?.offsetSec ?? 0}
              onChange={e => active && burnPatch(active.id, { offsetSec: Number(e.target.value) })} style={{ flex: 1, accentColor: '#ff3b30' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {(active?.offsetSec ?? 0) < 0 ? '−' : '+'}{Math.floor(Math.abs(active?.offsetSec ?? 0) / 60)}:{String(Math.abs(active?.offsetSec ?? 0) % 60).padStart(2, '0')}
            </span>
          </div>
        </div>
      )}

      {/* Export */}
      {readyVideos.length > 0 && (
        <div style={card}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' }}>Exportar</p>
          {exporting ? (() => {
            const enFila = videos.filter(v => v.exportState === 'pendiente').length
            const total  = doneCount + enFila + 1
            return (
            <div>
              {total > 1 && (
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', margin: '0 0 8px' }}>
                  Video {Math.min(doneCount + 1, total)} de {total}{enFila > 0 ? ` · ${enFila} en fila` : ''}
                </p>
              )}
              <div style={{ height: 8, background: 'var(--chip-bg)', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                <div style={{ height: '100%', width: `${Math.round(progress * 100)}%`, background: '#ff3b30', transition: 'width 0.2s' }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                Procesando en tiempo real… {Math.round(progress * 100)}% — podés seguir cargando videos o cambiar de pestaña, la fila sigue sola (no cierres el navegador)
              </p>
            </div>
            )
          })() : (
            <>
              {(() => {
                const pendientes = readyVideos.filter(v => v.exportState !== 'listo').length
                const n = pendientes || readyVideos.length
                const otraVez = pendientes === 0
                return (
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <button onClick={() => runBatch('burn')} style={{ flex: 1, padding: '12px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: '#ff3b30', color: '#fff' }}>
                  {otraVez ? 'Volver a quemar' : 'Quemar'} {n > 1 ? `los ${n} videos` : otraVez ? '' : 'en el video'}
                </button>
                <button onClick={() => runBatch('overlay')} style={{ flex: 1, padding: '12px', borderRadius: 11, border: '1px solid var(--field-border)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: 'var(--card-bg)', color: 'var(--text)' }}>
                  Overlay {n > 1 ? `× ${n}` : ''}
                </button>
              </div>
                )
              })()}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Fondo del overlay:</span>
                {[['transparent', 'Transparente'], ['green', 'Verde (chroma)'], ['black', 'Negro']].map(([k, l]) => (
                  <button key={k} onClick={() => setBgMode(k)} style={{ padding: '4px 9px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: bgMode === k ? 'var(--btn-strong)' : 'var(--chip-bg)', color: bgMode === k ? 'var(--seg-active-text-inv)' : 'var(--text-muted)' }}>{l}</button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-soft)', margin: '10px 0 0', lineHeight: 1.5 }}>
                Sale en <strong>.webm</strong> y tarda lo que duran los videos (se procesan en fila, en tiempo real — cada uno se descarga al terminar{readyVideos.length > 1 ? '; si el navegador pregunta, permití descargas múltiples' : ''}). El overlay transparente anda en CapCut/DaVinci/After Effects; si tu editor no lo toma, usá <strong>Verde (chroma)</strong>. Recomendado: Chrome.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Descarga de datos en SEGUNDO PLANO ────────────────────────────────────────
// El pedido vive a nivel módulo: podés cambiar de pestaña y sigue solo; también
// se puede cancelar. Cuando termina, dispara la descarga del archivo.
